import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildSegmentEditPath } from '../../../../app-routes';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { DEFAULT_SEGMENT_TEMPLATES } from '../default-segment-templates';
import {
  useAvailableFields,
  useCreateSegmentFromTemplate,
} from '../useSegmentTemplates';
import { BaseSegmentTemplateSelection } from './BaseSelection';
import styles from './CreateSegmentDialog.module.css';
import { SegmentTemplateForm } from './SegmentTemplateForm';

interface CreateSegmentDialogProps {
  onClose: () => void;
}

export const CreateSegmentDialog: React.FC<CreateSegmentDialogProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const {
    data: availableFields = [],
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useAvailableFields();
  const [selectedTemplateName, setSelectedTemplateName] = useState<
    string | null
  >(null);
  const [segmentName, setSegmentName] = useState('');
  const [description, setDescription] = useState('');
  const [contentsJson, setContentsJson] = useState('');
  const [isJsonValid, setIsJsonValid] = useState(true);

  const handleClose = () => {
    setSelectedTemplateName(null);
    setSegmentName('');
    setDescription('');
    setContentsJson('');
    setIsJsonValid(true);
    onClose();
  };

  const handleSelectTemplate = (templateName: string) => {
    setSelectedTemplateName(templateName);
    const template = DEFAULT_SEGMENT_TEMPLATES.find(
      t => t.name === templateName,
    );
    setSegmentName(template?.displayName || '');
    setDescription(template?.description || '');
    setContentsJson(JSON.stringify(template?.contents || [], null, 2));
  };

  const createFromTemplateMutation = useCreateSegmentFromTemplate({
    onSuccess: canonicalVersionId => {
      navigate(buildSegmentEditPath(canonicalVersionId));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplateName || !isJsonValid) return;

    const contents = JSON.parse(contentsJson);
    createFromTemplateMutation.mutate({
      name: segmentName,
      description: description,
      contents,
    });
  };

  const isLoading = createFromTemplateMutation.isPending;
  const selectedTemplate = selectedTemplateName
    ? DEFAULT_SEGMENT_TEMPLATES.find(t => t.name === selectedTemplateName)
    : null;

  return (
    <div className={styles.createSegmentDialog}>
      <DialogBackdrop onClose={handleClose} />

      <div className={styles.dialogWrapper}>
        <div className={styles.dialogContent}>
          {!selectedTemplateName ? (
            <BaseSegmentTemplateSelection
              templates={DEFAULT_SEGMENT_TEMPLATES}
              onSelectTemplate={handleSelectTemplate}
              onClose={handleClose}
            />
          ) : (
            <SegmentTemplateForm
              segmentName={segmentName}
              description={description}
              contentsJson={contentsJson}
              availableFields={availableFields}
              isLoadingFields={isLoadingFields}
              fieldsError={fieldsError}
              onSegmentNameChange={setSegmentName}
              onDescriptionChange={setDescription}
              onContentsChange={setContentsJson}
              onValidationChange={setIsJsonValid}
              onSubmit={handleSubmit}
              onCancel={handleClose}
              onClose={handleClose}
              isLoading={isLoading}
              templateName={selectedTemplate?.displayName}
            />
          )}
        </div>
      </div>
    </div>
  );
};
